import React, {useEffect, useState} from 'react';
import {DialogTitle, DialogContent, Stack, Button, Typography, Link, IconButton, Divider, Box} from "@mui/material";
import CreateAccountForm from '../forms/CreateAccountForm';
import {Formik, Form} from 'formik';
import createAccountModel, {createAccountModelType} from '../forms/models/createAccountModel';
import createAccountSchema from '../forms/schemas/createAccountSchema';
import * as Yup from 'yup';
import {usePostUsersMutation, UserWithPassword} from '../../redux/vmsApi';
import LoadingCover from '../common/LoadingCover';
import authSlice from "../../redux/reducers/authSlice";
import {useAppDispatch, useAppSelector} from "../../redux/hooks";
import {Close, PersonAddOutlined} from '@mui/icons-material';
import useIsMobile from '../../hooks/useIsMobile';
import { styled } from '@mui/material/styles';

const { formField: {createAccountEmail} } = createAccountModel;

type MyFormValues = createAccountModelType;

const createAccountModelInitial = createAccountModel.initialValues;

const CreateAccountFormSchema = Yup.object().shape({
    ...createAccountSchema,
    createAccountGender: Yup.string().required(createAccountModel.formField.createAccountGender.requiredErrorMsg),
});

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
    padding: '24px 24px 16px',
    backgroundColor: theme.palette.primary.main,
    color: 'white',
}));

const IconContainer = styled(Box)({
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '16px',
});

const SignupIcon = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.primary.dark,
    borderRadius: '50%',
    padding: '12px',
    marginTop: '10px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
}));

const StyledButton = styled(Button)(() => ({
    padding: '12px',
    borderRadius: '8px',
    fontWeight: 600,
    textTransform: 'none',
    boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)',
    transition: 'all 0.3s ease',
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 6px 15px rgba(0, 0, 0, 0.15)',
    },
}));

interface OwnProps {
    close: () => void;
    showLoginMsg?: boolean;
}

const CreateAccount = (props: OwnProps) => {
    const { close, showLoginMsg = true } = props;
    const dispatch = useAppDispatch();
    const isMobile = useIsMobile();
    const [updateUserCreate, { isSuccess, isLoading, isError, error }] = usePostUsersMutation();
    const [createError, setCreateError] = useState('');
    const initialEmail = useAppSelector(state => state.auth.initialEmail);
    const authMessage = useAppSelector(state => state.auth.authMessage);

    const initialValues: MyFormValues = {
        ...createAccountModelInitial,
        [createAccountEmail.name]: initialEmail,
    };

    const handleSubmit = async (values: MyFormValues) => {
        const data = {
            name: values.createAccountName,
            department: values.createdAccountDepartment,
            email: values.createAccountEmail,
            password: values.createAccountPass,
            gender: values.createAccountGender, // Include gender in submission
        } as UserWithPassword;
        await updateUserCreate({ userWithPassword: data });
    };

    useEffect(() => {
        if (isSuccess) {
            setTimeout(() => {
                close();
            }, 300);
        }
    }, [isSuccess, close]);

    useEffect(() => {
        if (isError && error) {
            const err: any = error;
            if (err?.data?.error?.message) {
                setCreateError(err?.data?.error?.message);
            }
        } else {
            setCreateError('');
        }
    }, [isError, error]);

    function showLogin() {
        dispatch(authSlice.actions.setAuthState('login'));
    }

    return (
        <React.Fragment>
            <StyledDialogTitle
                data-cy="dialogue-title"
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: isMobile ? 'space-between' : 'center',
                }}
            >
                <Typography variant="h5" fontWeight="600" color={'white'}>
                    Create Account
                </Typography>
                {isMobile ? (
                    <IconButton onClick={close} sx={{ color: 'white' }}>
                        <Close sx={{ fontWeight: 'bold', fontSize: 24 }} />
                    </IconButton>
                ) : null}
            </StyledDialogTitle>

            <DialogContent sx={{ padding: '24px', paddingBottom: '32px' }}>
                <IconContainer>
                    <SignupIcon>
                        <PersonAddOutlined sx={{ color: 'white', fontSize: 28 }} />
                    </SignupIcon>
                </IconContainer>
                
                <Typography 
                    align="center" 
                    variant="h6" 
                    fontWeight="500" 
                    sx={{ marginBottom: '16px' }}
                >
                    Join WestProp Operations Hub
                </Typography>
                
                {authMessage && (
                    <Typography 
                        align="center" 
                        variant="body2" 
                        color="primary" 
                        sx={{ marginBottom: '16px' }}
                    >
                        {authMessage}
                    </Typography>
                )}
                
                <Formik
                    enableReinitialize
                    initialValues={initialValues}
                    onSubmit={handleSubmit}
                    validationSchema={CreateAccountFormSchema}
                >
                    <Form>
                        <Stack spacing={4} width="100%" alignItems="center">
                            <CreateAccountForm apiError={createError} />
                            <StyledButton
                                variant="contained"
                                type="submit"
                                fullWidth
                                size="large"
                                data-cy={"createAccountSubmit"}
                            >
                                Create Account
                            </StyledButton>
                            
                            {showLoginMsg && (
                                <>
                                    <Divider sx={{ width: '100%', margin: '8px 0' }} />
                                    <Typography align="center" variant="body2">
                                        Already have an account? {' '}
                                        <Link 
                                            onClick={showLogin}
                                            sx={{ 
                                                cursor: 'pointer',
                                                textDecoration: 'none',
                                                '&:hover': { textDecoration: 'underline' }
                                            }}
                                        >
                                            Sign in
                                        </Link>
                                    </Typography>
                                </>
                            )}
                        </Stack>
                    </Form>
                </Formik>
            </DialogContent>

            {(isLoading || isSuccess) && <LoadingCover isSuccess={isSuccess} />}
        </React.Fragment>
    );
};

export default CreateAccount;
